import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom/";
import { checkPermisos } from "../../utils/checkPermisos.js";
import { useAuth } from "../../../AuthContext";

import Joi from "joi-browser";
import { use } from "react";
import { set } from "lodash";

/* ---------- Joi helpers ---------- */

function joiErrorsToFieldMap(error) {
  if (!error) return {};
  const out = {};
  for (const detail of error.details) {
    const key = detail.path.join(".") || "_form";
    if (!out[key]) out[key] = detail.message;
  }
  return out;
}

/** Validate the whole object. */
function validateAll(schema, data) {
  if (!schema) return {};
  const { error } = schema.validate(data, {
    abortEarly: false,
    allowUnknown: true,
    stripUnknown: false,
  });
  return joiErrorsToFieldMap(error);
}

/**
 * Validate a single field. Returns the error message string, or null if valid.
 * Uses schema.extract() to pull out just that field's sub-schema so we don't
 * re-validate the whole object on every keystroke.
 */
function validateField(schema, field, value) {
  if (!schema) return null;
  let fieldSchema;
  try {
    fieldSchema = schema.extract(field);
  } catch {
    // Field isn't in the schema — treat as no rule.
    return null;
  }
  const { error } = fieldSchema.validate(value, { abortEarly: true });
  return error ? error.details[0].message : null;
}

export function useCrudForm({
  defaultPath,
  initialData = null,
  onSave,
  onDelete,
  schema,
  validate,
  validateOne,
  transformBeforeSave,
  transformAfterSave,
  checkBeforeDelete,
  hookWillMount,
  hookFetchData,
  purifyInitialData,
  onSuccess,
  twoStepSave = false,
  liveValidate = true,
  liveValidateForm = true,
  onLoading,
  onDoneLoading,
}) {
  const touchedRef = useRef(new Set());
  const history = useHistory();
  const location = useLocation();

  const { activeUser, permisos: allowedTo } = useAuth();

  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [action, setAction] = useState("C");
  const [mounted, setMounted] = useState(false);
  const [child, setChild] = useState({});
  const [childIndex, setChildIndex] = useState(0);
  const [childPage, setChildPage] = useState(1);
  const [childAction, setChildAction] = useState("C");
  const [isEditingChild, setIsEditingChild] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [menuLink, setMenuLink] = useState("/");
  const [saving, setSaving] = useState(false);
  const [closeOnExit, setCloseOnExit] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [permisos, setPermisos] = useState({});
  const { id } = useParams();

  /*------------*/

  const useComponentWillMount = (func) => {
    const willMount = useRef(true);
    if (willMount.current) {
      func();
      setMounted(true);
      willMount.current = false;
    }
  };

  const checkIfCan = (action, permisos) => {
    if (action === "E" && !permisos.canModify) return false;
    if (action === "M" && !permisos.canModify) return false;
    if (action === "D" && !permisos.canDelete) return false;
    if (action === "B" && !permisos.canDelete) return false;
    return true;
  };

  useComponentWillMount(async () => {
    const allowed = checkPermisos(defaultPath, allowedTo, activeUser, history);
    let action = location.state && location.state.action ? location.state.action : "C";
    if (id.includes("nuev")) action = "A";
    if (id === "nuevo_" || id === "nueva_") setCloseOnExit(true);
    if (!checkIfCan(action, allowed)) action = "C";
    setPermisos(allowed);
    setAction(action);
    if (hookWillMount) await hookWillMount();
    if (id.includes("nuev")) {
      await hookFetchData(null);
      const newData = purifyInitialData ? await purifyInitialData(initialData) : initialData;
      setData(newData);
    } else {
      if (hookFetchData) {
        if (onLoading) onLoading();
        const record = await hookFetchData(id);
        setData(record);
        if (onDoneLoading) onDoneLoading();
      }
    }
  });

  const goBack = () => {
    history.goBack();
  };

  /* Effective validators (custom function wins over schema). */
  const effectiveValidateAll = useMemo(() => {
    if (validate) return (d) => validate(d) || {};
    return (d) => validateAll(schema, d);
  }, [validate, schema]);

  const effectiveValidateField = useMemo(() => {
    if (validateOne) return (field, value, d) => validateOne(field, value, d) || null;
    return (field, value) => validateField(schema, field, value);
  }, [validateOne, schema]);

  const handleChange = useCallback(
    (e) => {
      const { currentTarget: input } = e;
      const field = input.name;
      const value = input.type === "checkbox" ? input.checked : input.value;
      setData((prev) => {
        const next = { ...prev, [field]: value };
        if (liveValidate) {
          const message = effectiveValidateField(field, value, next);
          setErrors((prevErrors) => {
            const nextErrors = { ...prevErrors };
            // Clear any prior server-level form error once the user is editing.
            delete nextErrors._form;
            if (message) {
              // Only show errors for touched fields, OR if the field already
              // had an error (e.g. from submit) — in that case we want to
              // update/clear it live.
              if (touchedRef.current.has(field) || prevErrors[field]) {
                nextErrors[field] = message;
              }
            } else {
              delete nextErrors[field];
            }
            return nextErrors;
          });
        } else if (errors[field]) {
          // Without live validation, still clear stale errors as the user types.
          setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
        }

        return next;
      });
    },
    [liveValidate, effectiveValidateField, errors],
  );

  const handleEditingChildArray = (isEditingChild) => {
    setIsEditingChild(isEditingChild);
  };

  const handleSelectChange = useCallback(
    (selectedOption, name) => {
      const field = name.name;
      const value = selectedOption.value;
      setData((prev) => {
        const next = { ...prev, [field]: selectedOption };
        if (liveValidate) {
          const message = effectiveValidateField(field, value, next);
          setErrors((prevErrors) => {
            const nextErrors = { ...prevErrors };
            // Clear any prior server-level form error once the user is editing.
            delete nextErrors._form;
            if (message) {
              // Only show errors for touched fields, OR if the field already
              // had an error (e.g. from submit) — in that case we want to
              // update/clear it live.
              if (touchedRef.current.has(field) || prevErrors[field]) {
                nextErrors[field] = message;
              }
            } else {
              delete nextErrors[field];
            }
            return nextErrors;
          });
        } else if (errors[field]) {
          // Without live validation, still clear stale errors as the user types.
          setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
        }

        return next;
      });
    },
    [liveValidate, effectiveValidateField, errors],
  );
  /* ---------- Blur handler ---------- */

  /**
   * Mark a field as touched and validate it. Wire to <input onBlur={() => handleBlur('name')} />.
   */
  const handleBlur = useCallback(
    (e) => {
      const { currentTarget: input } = e;
      const field = input.name;
      touchedRef.current.add(field);
      if (!liveValidate) return;
      const message = effectiveValidateField(field, data[field], data);
      setErrors((prev) => {
        const next = { ...prev };
        if (message) next[field] = message;
        else delete next[field];
        return next;
      });
    },
    [liveValidate, effectiveValidateField, data],
  );

  const handleSelectBlur = useCallback(
    (name) => {
      const field = name;
      touchedRef.current.add(field);
      if (!liveValidate) return;
      const message = effectiveValidateField(field, data[field], data);
      setErrors((prev) => {
        const next = { ...prev };
        if (message) next[field] = message;
        else delete next[field];
        return next;
      });
    },
    [liveValidate, effectiveValidateField, data],
  );

  /**
   * submit() does the actual work without touching the DOM event, so it can
   * be called directly by an override (the equivalent of `super.handleSubmit`).
   */
  const submit = useCallback(async () => {
    if (liveValidate) {
      const validationErrors = effectiveValidateAll(data) || {};
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return false;
      }
    }

    const payload = transformBeforeSave ? transformBeforeSave(data) : data;
    // console.log(payload);

    if (onLoading) onLoading();
    setErrors({});
    try {
      const newId = await onSave(payload);
      onSuccess?.(payload);
      if (twoStepSave && action === "A") {
        const newData = { ...data, _id: newId };
        // setData((prev) => ({ ...prev, _id: newId }));
        setData(newData);

        const payload = transformAfterSave ? transformAfterSave(newData) : newData;
        setData(payload);
        setAction("M");
      } else {
        if (closeOnExit) window.close();
        else goBack();
      }
      return true;
    } catch (err) {
      // Expect the API to throw { fields: { fieldName: "message" } }.
      setErrors(err?.fields || { _form: err?.message || "Something went wrong." });
      throw err;
      return false;
    } finally {
      if (onDoneLoading) onDoneLoading();
    }
  }, [data, validate, transformBeforeSave, onSave, onSuccess]);

  /** handleSubmit() is the event-handler wrapper you pass to <form onSubmit>. */
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      return submit();
    },
    [submit],
  );

  const reset = useCallback(() => {
    setData(initialData);
    setErrors({});
  }, [initialData]);

  return {
    data,
    isEditingChild,
    errors,
    action,
    mounted,
    permisos,
    // setters (exposed so a form can fully override behavior if needed)
    setData,
    setErrors,
    setAction,
    // actions
    handleChange,
    handleSelectChange,
    handleBlur,
    handleSelectBlur,
    handleSubmit,
    handleEditingChildArray,
    goBack,
    submit,
    reset,
  };
}
